import { z } from 'zod';
import { optionalString } from './OptionalString';
import { AssociatedAccount, associatedAccountSchema } from './Account';
import { AssociatedCampaign, associatedCampaignSchema } from './Campaign';
import { AssociatedContact, associatedContactSchema } from './Contact';
import { optionalDate } from './Scheduling';

const relationshipTypeValues = [
  'Account',
  'Contact',
  'Document',
  'Lead',
  'Opportunity',
  'Inventory',
] as const;

export const RelationshipTypes = z.enum(relationshipTypeValues);

const availableViews = [
  'accounts',
  'contacts',
  'documents',
  'leads',
  'opportunities',
] as const;

export const viewToRelationshipType = z.enum(availableViews).transform(view => {
  switch (view) {
    case 'accounts':
      return 'Account';
    case 'contacts':
      return 'Contact';
    case 'documents':
      return 'Document';
    case 'leads':
      return 'Lead';
    case 'opportunities':
      return 'Opportunity';
    default:
      return undefined;
  }
});

export const relationshipTypeToView = z.enum(relationshipTypeValues).transform(type => {
  switch (type) {
    case 'Account':
      return 'accounts';
    case 'Contact':
      return 'contacts';
    case 'Document':
      return 'documents';
    case 'Lead':
      return 'leads';
    case 'Opportunity':
      return 'opportunities';
    default:
      throw new Error(`Invalid relationship type '${type}'`);
  }
});

export const objectNamePluralMap: Record<RelationshipType, string> = {
  Account: 'Accounts',
  Contact: 'Contacts',
  Document: 'Documents',
  Lead: 'Leads',
  Opportunity: 'Opportunities',
  Inventory: 'Inventory',
};

export const relationshipSchema = z.object({
  id: z.number(),
  name: optionalString,
  type: RelationshipTypes.optional(),
  createdAt: optionalDate,
  updatedAt: optionalDate,
});

export const contactRelationshipSchema = relationshipSchema.extend({
  isPrimaryContact: z.boolean().optional(),
});

export const contactToRelationship = associatedContactSchema.transform(contact => ({
  id: contact.id,
  name: contact.fullName ?? `${contact.firstName ?? ''}${contact.lastName ? ' ' + contact.lastName : ''}`,
  type: 'Contact' as RelationshipType,
  isPrimaryContact: Boolean(contact.contactLabels?.find(label => label.contactLabelId === 1)),
  createdAt: contact.createdAt,
  updatedAt: contact.updatedAt,
}));

export const accountToRelationship = associatedAccountSchema.transform(account => ({
  id: account.c2Id,
  name: account.c2Name,
  type: 'Account' as RelationshipType,
  createdAt: account.createdAt,
  updatedAt: account.updatedAt,
}));

export const campaignToRelationship = associatedCampaignSchema.transform(campaign => ({
  id: campaign.id,
  name: campaign.name,
  type: campaign.type as RelationshipType,
  status: campaign.status,
  accountId: campaign.accountId,
  createdAt: campaign.createdAt,
  updatedAt: campaign.updatedAt,
}));

export const mapCampaignsToIndividualTypes = z.array(associatedCampaignSchema).transform(campaigns => ({
  Lead: campaigns?.filter(camp => camp.type === 'Lead')?.map(camp => campaignToRelationship.parse(camp)) satisfies Relationship[],
  Opportunity: campaigns?.filter(camp => camp.type === 'Opportunity')?.map(camp => campaignToRelationship.parse(camp)) satisfies Relationship[],
}));

export type Relationship = z.infer<typeof relationshipSchema>;
export type RelationshipType = z.infer<typeof RelationshipTypes>;
export type ContactRelationship = z.infer<typeof contactRelationshipSchema>;

// This is just for better semantic clarity in cases where we need the type name, but not in the context of in relation to another object
export type CrmObjectName = RelationshipType;

export type RelationshipRecord = {
  [K in RelationshipType]?: Relationship[];
}

export type AssociationsResponse = {
  data: {
    accounts: AssociatedAccount[],
    campaigns: AssociatedCampaign[],
    contacts: AssociatedContact[],
  }
}
